import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Images, Play, MoveHorizontal } from "lucide-react";
import { Facility } from "@/hooks/useFacilities";
import { Carousel, CarouselContent, CarouselItem, CarouselApi } from "@/components/ui/carousel";

interface FacilitiesSectionProps {
  facilities: Facility[] | undefined;
  facilitiesLoading: boolean;
  providerSlug?: string;
}

const FacilitiesSection = ({ facilities, facilitiesLoading, providerSlug }: FacilitiesSectionProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [playingVideoId, setPlayingVideoId] = useState<string | null>(null);

  if (facilitiesLoading) {
    return (
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4 text-foreground">Our Facilities</h2>
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 sm:grid sm:grid-cols-2 sm:gap-4 sm:overflow-visible">
          {[1, 2].map((i) => (
            <div key={i} className="animate-pulse flex-shrink-0 w-64 sm:w-auto">
              <div className="h-36 sm:h-48 bg-muted rounded-xl mb-3"></div>
              <div className="h-4 bg-muted rounded mb-2"></div>
              <div className="h-4 bg-muted rounded w-3/4"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!facilities || facilities.length === 0) {
    return null;
  }

  const currentFacility = facilities[currentIndex];

  const renderFacilityMedia = (facility: Facility, height: string) => {
    const isPlaying = playingVideoId === facility.id;

    if (isPlaying && facility.video_url) {
      return (
        <div className={`relative ${height}`}>
          <video
            src={facility.video_url}
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover bg-black"
          />
        </div>
      );
    }

    return (
      <div 
        className={`relative ${facility.video_url ? 'cursor-pointer group' : ''}`}
        onClick={() => facility.video_url && setPlayingVideoId(facility.id)}
      >
        {facility.image_url ? (
          <img 
            src={facility.image_url} 
            alt={facility.name}
            className={`w-full object-cover ${height}`}
          />
        ) : (
          <div className={`bg-muted ${height}`}></div>
        )}
        {facility.video_url && (
          <div className="absolute inset-0 bg-black/20 flex items-center justify-center group-hover:bg-black/30 transition-colors">
            <div className="bg-white/70 rounded-full p-2 shadow-md">
              <Play className="h-5 w-5 text-primary fill-primary" />
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="mb-8">
      <h2 className="text-xl font-semibold mb-4 text-foreground">Our Facilities</h2>
      
      {/* Mobile: Swipeable carousel */}
      <div className="sm:hidden">
        <Carousel 
          className="w-full"
          setApi={(api) => {
            api?.on('select', () => {
              setCurrentIndex(api.selectedScrollSnap());
              setPlayingVideoId(null);
            });
          }}
        >
          <CarouselContent>
            {facilities.map((facility, index) => (
              <CarouselItem key={facility.id}>
                <Card className="overflow-hidden border-border bg-card relative">
                  {renderFacilityMedia(facility, "h-48")}
                  
                  {facilities.length > 1 && (
                    <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-background/80 backdrop-blur-sm rounded-full px-2.5 py-1 text-xs font-medium shadow-md z-10">
                      <MoveHorizontal className="h-3 w-3 text-muted-foreground" />
                      <span>{index + 1} / {facilities.length}</span>
                    </div>
                  )}
                  
                  <CardContent className="p-3">
                    <h3 className="font-semibold text-foreground mb-1 text-sm">{facility.name}</h3>
                    {facility.description && (
                      <p className="text-muted-foreground text-xs line-clamp-2">{facility.description}</p>
                    )}
                  </CardContent>
                </Card>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </div>
      
      {/* Desktop: Grid layout */}
      <div className="hidden sm:grid sm:grid-cols-2 sm:gap-4">
        {facilities.map((facility) => (
          <Card 
            key={facility.id} 
            className="overflow-hidden border-border hover:shadow-md transition-shadow bg-card"
          >
            {renderFacilityMedia(facility, "h-40")}
            <CardContent className="p-4">
              <h3 className="font-semibold text-foreground mb-1">{facility.name}</h3>
              {facility.description && (
                <p className="text-muted-foreground text-sm line-clamp-2">{facility.description}</p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default FacilitiesSection;
